import React, { useMemo, useState } from "react";
import { BACKEND_URL } from "../../config/env";

const endpoint = `${String(BACKEND_URL || "").replace(/\/+$/, "")}/public/facilweb/rut-onboarding`;

const parseRequest = () => {
  const query = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const returnOrigin = query.get("return_origin") || "";
  let validOrigin = false;
  try {
    const parsed = new URL(returnOrigin);
    validOrigin = ["http:", "https:"].includes(parsed.protocol) && parsed.origin === returnOrigin;
  } catch (_error) {}
  return {
    nit: (query.get("nit") || "").replace(/\D/g, ""),
    branch: query.get("branch_code") || "",
    state: query.get("state") || "",
    challenge: query.get("code_challenge") || "",
    returnOrigin: validOrigin ? returnOrigin : ""
  };
};

const FacilwebApiAuthorization = () => {
  const request = useMemo(parseRequest, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(!localStorage.getItem("token"));

  const validRequest = Boolean(
    window.opener && request.returnOrigin && /^\d{6,15}$/.test(request.nit) &&
    /^[A-Za-z0-9_-]{43}$/.test(request.state) && /^[A-Za-z0-9_-]{43}$/.test(request.challenge)
  );

  const fail = message => {
    setError(true);
    setStatus(message);
    if (window.opener && request.returnOrigin) {
      window.opener.postMessage({ type: "FACILWEB_SOLUWHATS_AUTH", state: request.state, error: message }, request.returnOrigin);
    }
  };

  const authorize = async event => {
    event.preventDefault();
    if (!validRequest) return fail("La solicitud no es válida o ya venció. Cierre esta ventana e intente nuevamente desde FACILWEB.");
    setBusy(true);
    setError(false);
    setStatus("Validando su cuenta y las API asociadas a este NIT...");
    try {
      let token = localStorage.getItem("token") || "";
      if (needsLogin || !token) {
        const login = await fetch(`${String(BACKEND_URL || "").replace(/\/+$/, "")}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ email: email.trim(), password })
        });
        const loginBody = await login.json();
        if (!login.ok || !loginBody.token) throw new Error(loginBody.message || "No fue posible iniciar sesión en SoluWhats.");
        token = loginBody.token;
        setPassword("");
      }

      const response = await fetch(`${endpoint}/authorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nit: request.nit, branch_code: request.branch, code_challenge: request.challenge })
      });
      const body = await response.json();
      if (!response.ok || !body.success || !body.data?.code) {
        const authorizationError = new Error(body.message || "SoluWhats no pudo autorizar este NIT.");
        authorizationError.status = response.status;
        throw authorizationError;
      }

      window.opener.postMessage({ type: "FACILWEB_SOLUWHATS_AUTH", state: request.state, code: body.data.code }, request.returnOrigin);
      setStatus("Autorización completada. Las credenciales se entregaron de forma cifrada al servidor de FACILWEB.");
      setTimeout(() => window.close(), 900);
    } catch (cause) {
      fail(cause.message || "No fue posible completar la autorización.");
      if (!localStorage.getItem("token") || [401, 403].includes(cause.status)) setNeedsLogin(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, background: "linear-gradient(145deg,#eaf6fb,#f7fbfd 55%,#e8f5f1)", fontFamily: "'Lato',sans-serif", color: "#17324d" }}>
      <section style={{ width: "min(100%,520px)", padding: 32, background: "#fff", border: "1px solid #c8dce8", borderRadius: 18, boxShadow: "0 18px 55px #17324d20" }}>
        <div style={{ color: "#0786b4", fontSize: 11, fontWeight: 800, letterSpacing: ".16em" }}>CONEXIÓN SEGURA</div>
        <h1 style={{ margin: "8px 0", fontSize: 25 }}>Autorizar FACILWEB PRO</h1>
        <p style={{ lineHeight: 1.55, color: "#526b82" }}>Se consultarán únicamente las API donde está registrado el NIT <strong>{request.nit || "no válido"}</strong>. Las credenciales no se mostrarán ni se enviarán al navegador de FACILWEB.</p>
        {request.branch && <p style={{ color: "#526b82" }}>Sucursal consultada: <strong>{request.branch}</strong></p>}
        {!validRequest && <p role="alert" style={{ padding: 12, borderRadius: 10, background: "#fff0ee", color: "#b9342d" }}>La solicitud no es válida o venció. Cierre esta ventana e inicie la autorización nuevamente.</p>}
        <form onSubmit={authorize}>
          {!needsLogin && localStorage.getItem("token") && <button type="button" onClick={() => setNeedsLogin(true)} style={{ marginTop: 8, padding: 0, border: 0, color: "#087fae", background: "transparent", fontWeight: 700, cursor: "pointer" }}>Iniciar sesión con otra cuenta de SoluWhats</button>}
          {needsLogin && <>
            <label style={{ display: "block", margin: "14px 0 6px", fontWeight: 700 }} htmlFor="soluwhats-email">Correo de SoluWhats</label>
            <input id="soluwhats-email" type="email" required autoComplete="username" value={email} onChange={event => setEmail(event.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: 13, border: "1px solid #bfd3e1", borderRadius: 9 }} />
            <label style={{ display: "block", margin: "14px 0 6px", fontWeight: 700 }} htmlFor="soluwhats-password">Contraseña de SoluWhats</label>
            <input id="soluwhats-password" type="password" required autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: 13, border: "1px solid #bfd3e1", borderRadius: 9 }} />
          </>}
          <button type="submit" disabled={!validRequest || busy} style={{ width: "100%", marginTop: 22, padding: 14, border: 0, borderRadius: 10, background: "#0788b8", color: "#fff", fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Verificando..." : needsLogin ? "Iniciar sesión y autorizar APIs" : "Autorizar APIs detectadas"}
          </button>
        </form>
        {status && <p role={error ? "alert" : "status"} style={{ marginTop: 16, color: error ? "#b9342d" : "#087d5b", lineHeight: 1.45 }}>{status}</p>}
        <p style={{ margin: "18px 0 0", fontSize: 12, color: "#687f93" }}>La autorización es temporal, de un solo uso y se limita a su cuenta de SoluWhats.</p>
      </section>
    </main>
  );
};

export default FacilwebApiAuthorization;
